import { useCallback, useContext } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { clearAllPersistedDataTableFilters } from "@bcc/components/data-table/use-data-table-persistence";
import { AuthContext } from "@bcc/contexts/auth-context";
import { BatchesContext } from "@bcc/contexts/batches-context";
import { RBACContext } from "@bcc/contexts/rbac-context";
import { useOptionalTour } from "@bcc/contexts/tour-context";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { sfdcLocalStorageKeys } from "@client-shared/providers/auth/types";
import { ConfigContext, configLocalStorageKeys } from "@client-shared/providers/config";
import { InstallationsContext } from "@client-shared/providers/installations";
import { clearShopifyOAuthState } from "@client-shared/providers/installations/shopifyOAuthStorage";
import { PayoutContext } from "@client-shared/providers/payout";
import { ProductsContext, productLocalStorageKeys } from "@client-shared/providers/products";
import { PurchaseOrdersContext } from "@client-shared/providers/purchase-orders";
import { ServicesContext } from "@client-shared/providers/services";
import { UserProfileContext } from "@client-shared/providers/user-profile";
import { useVendorContext, vendorLocalStorageKeys } from "@client-shared/providers/vendor";

export type StateCleanupReason = "logout" | "vendor-change";

export const VENDOR_SESSION_STORAGE_KEYS = [
  ...Object.values(vendorLocalStorageKeys),
  ...Object.values(configLocalStorageKeys),
  ...Object.values(productLocalStorageKeys),
  "isOnboarded",
  "connectedPlatform",
  "isAdmin",
  "roasterName",
];

function useClearVendorProviders(): () => void {
  const [, , vendorUtils] = useVendorContext();
  const clearVendorState = vendorUtils?.clearVendorState;
  const clearUserProfile = useContext(UserProfileContext)[2]?.clearState;
  const clearServices = useContext(ServicesContext)[2]?.clearState;
  const clearConfig = useContext(ConfigContext)[2]?.clearState;
  const clearInstallations = useContext(InstallationsContext)[2]?.clearState;
  const clearProducts = useContext(ProductsContext)[2]?.clearState;
  const clearPurchaseOrders = useContext(PurchaseOrdersContext)[2]?.clearState;
  const clearBatches = useContext(BatchesContext)?.[2]?.clearState;
  const clearPayout = useContext(PayoutContext)[2]?.clearState;
  const clearAuth = useContext(AuthContext)?.clearState;
  const clearRbac = useContext(RBACContext)?.clearState;
  const clearTour = useOptionalTour()?.clearState;
  return useCallback(() => {
    [
      clearVendorState,
      clearUserProfile,
      clearServices,
      clearConfig,
      clearInstallations,
      clearProducts,
      clearPurchaseOrders,
      clearBatches,
      clearPayout,
      clearAuth,
      clearRbac,
      clearTour,
    ].forEach((clearState) => clearState?.());
  }, [
    clearVendorState,
    clearUserProfile,
    clearServices,
    clearConfig,
    clearInstallations,
    clearProducts,
    clearPurchaseOrders,
    clearBatches,
    clearPayout,
    clearAuth,
    clearRbac,
    clearTour,
  ]);
}

/** Clears vendor data for switching and impersonation; logout also ends authentication. */
export function useClearAllState(): (reason: StateCleanupReason) => void {
  const queryClient = useQueryClient();
  const clearVendorProviders = useClearVendorProviders();
  const [, , authUtils] = useSFDCAuthState();
  const clearAuthProvider = authUtils?.clearAuthProvider;
  const logout = useContext(AuthContext)?.logout;
  return useCallback(
    (reason) => {
      VENDOR_SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
      clearAllPersistedDataTableFilters();
      clearShopifyOAuthState();
      clearVendorProviders();
      queryClient.clear();
      if (reason === "logout") {
        clearAuthProvider?.();
        logout?.();
        [...Object.values(sfdcLocalStorageKeys), "redirectUrl"].forEach((key) => localStorage.removeItem(key));
      }
    },
    [queryClient, clearVendorProviders, clearAuthProvider, logout]
  );
}
